/**
 * Waar een deeplink heen wijst, ongeacht hoe hij binnenkomt.
 *
 * Een gedeelde link bereikt de app langs twee wegen, en beide horen op hetzelfde pad uit te komen:
 *
 * - `https://api.heyboomer.nl/deeplink/<slug>/app/projects/<id>` — de pagina van de api, die de app
 *   probeert te openen
 * - `boomer-theedgeevents://app/projects/<id>` — het custom scheme dat diezelfde pagina als fallback
 *   aanroept
 *
 * Bij dat tweede geval zit het eerste segment van het pad in de hostname: de URL-parser leest `app`
 * als host en houdt `/projects/<id>` over. Wie die host niet terugplakt navigeert naar
 * `/projects/<id>`, en die route bestaat niet.
 *
 * **Herken een custom scheme aan wat het niet is.** Hier stond eerder een vergelijking met een uit
 * `VITE_ORGANIZATION_SLUG` afgeleide naam ("boomer-" plus de slug zonder streepjes). Die aanname
 * klopt niet: The Edge draait op slug `the-edge-planning` met scheme `boomer-theedgeevents`, dus de
 * vergelijking faalde, de hostname ging verloren en precies dat merk kreeg een wit scherm. Elk merk
 * registreert zijn scheme zelf in `ios/App/<naam>-Info.plist` en `android/app/build.gradle`; de
 * slug zegt daar niets over. Alles wat geen `http:` of `https:` is, is dus het scheme van deze
 * app.
 */
export interface DeeplinkTarget {
  /** Het pad binnen de app, altijd beginnend met een `/`. */
  path: string;
  /** De querystring inclusief `?`, of leeg. */
  search: string;
  /** De hash inclusief `#`, of leeg. */
  hash: string;
}

/**
 * Of deze hostname de host is waarop de webview zelf draait, en dus geen padsegment.
 *
 * Op iOS laadt Capacitor de app op `<server.iosScheme>://<server.hostname>`. De standaard daarvan
 * is `capacitor://localhost` (`InstanceDescriptorDefaults` in `@capacitor/ios`), maar
 * `capacitor.config.ts` zet `server.hostname` op `app.heyboomer.nl`. Dat staat in de basisconfig en
 * geen enkel merk overschrijft `server`, dus élk merk laadt op `capacitor://app.heyboomer.nl`.
 *
 * Hier stond die uitzondering als de letterlijke tekst `localhost`, precies de waarde die deze app
 * niet gebruikt. `capacitor://app.heyboomer.nl/app/projects/<id>` werd daardoor gelezen als een
 * deeplink met `app.heyboomer.nl` als eerste padsegment, kwam uit op
 * `/app.heyboomer.nl/app/projects/<id>`, en het vangnet in `global.router.tsx` zette de gebruiker
 * daarna op `/`.
 *
 * Vraag het daarom aan de webview zelf in plaats van het op te schrijven: `location.hostname` is de
 * host waarop deze code op dit moment draait, wat er ook in de config of in een `.env` van een merk
 * staat. `localhost` blijft daarnaast gelden, want dat is de origin in dev en de
 * Capacitor-standaard.
 */
function isEigenWebviewHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (host === "localhost") {
    return true;
  }

  // Zonder browser (een unit-test buiten jsdom, of een andere niet-webcontext) valt er niets te
  // vergelijken. Dan telt alleen `localhost` hierboven.
  const webviewHost =
    typeof globalThis.location === "undefined" ? "" : globalThis.location.hostname;

  return webviewHost !== "" && host === webviewHost.toLowerCase();
}

export function resolveDeeplinkPath(urlString: string): DeeplinkTarget | null {
  let url: URL;
  try {
    url = new URL(urlString);
  } catch {
    return null;
  }

  let path = url.pathname;

  // Een url met de eigen origin (`capacitor://app.heyboomer.nl/...`) is de app zelf: daar is de
  // hostname de host van de webview en niet het eerste padsegment. Zie `isEigenWebviewHost`.
  const isCustomScheme = url.protocol !== "http:" && url.protocol !== "https:";
  if (isCustomScheme && url.hostname && !isEigenWebviewHost(url.hostname)) {
    if (url.hostname === "accept-invitation") {
      path = "/auth/accept-invitation" + path;
    } else if (url.hostname === "transfer") {
      path = "/app/transfer" + path;
    } else {
      path = "/" + url.hostname + path;
    }
  }

  // De https-variant draagt het pad achter `/deeplink/<slug>/`. De slug is er voor de api, niet
  // voor de app: die weet zelf al welk merk hij is.
  const deeplinkMatch = path.match(/^\/deeplink\/[^/]+\/(.*)$/);
  if (deeplinkMatch) {
    const extractedPath = deeplinkMatch[1];
    if (extractedPath.startsWith("accept-invitation/")) {
      path = "/auth/" + extractedPath;
    } else if (extractedPath.startsWith("transfer/")) {
      path = "/app/" + extractedPath;
    } else {
      path = "/" + extractedPath;
    }
  }

  return { path, search: url.search, hash: url.hash };
}
